import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useGenerateAttendanceSessionCSV } from '~entities/classes/api/generate-attendance-session-csv';
import { useGenerateAttendanceSessionPDF } from '~entities/classes/api/generate-attendance-session-pdf';
import { useGetAllAttendanceSessionPDFJobs } from '~entities/classes/api/get-all-attendance-session-pdf-jobs';
import { useGetAttendanceSessionCSVJobStatus } from '~entities/classes/api/get-attendance-session-csv-job-status';
import { useGetAttendanceSessionPDFJobStatus } from '~entities/classes/api/get-attendance-session-pdf-job-status';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { SessionSelector } from '~features/shared/session-selector';

import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Progress } from '~shared/shadcn/ui/progress';

type ExportFormat = 'pdf' | 'csv';

interface DownloadAttendanceSheetsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DownloadAttendanceSheetsDialog = ({
  open,
  onOpenChange,
}: DownloadAttendanceSheetsDialogProps) => {
  const [selectedSession, setSelectedSession] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
    | null
  >(null);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('pdf');
  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [csvJobId, setCsvJobId] = useState<string | null>(null);
  const [showBalance, setShowBalance] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [csvDownloaded, setCsvDownloaded] = useState(false);

  // Mutations for starting jobs
  const generatePDFMutation = useGenerateAttendanceSessionPDF();
  const generateCSVMutation = useGenerateAttendanceSessionCSV();

  // Job status queries
  const { data: pdfJobStatus, refetch: refetchPdfStatus } =
    useGetAttendanceSessionPDFJobStatus({
      jobId: pdfJobId ?? '',
    });

  const { data: csvJobStatus, refetch: refetchCsvStatus } =
    useGetAttendanceSessionCSVJobStatus({
      jobId: csvJobId ?? '',
    });

  // Get all jobs for history
  const { data: allJobs, refetch: refetchAllJobs } =
    useGetAllAttendanceSessionPDFJobs();

  // Determine current job status based on format
  const currentJobStatus = exportFormat === 'pdf' ? pdfJobStatus : csvJobStatus;
  const currentJobId = exportFormat === 'pdf' ? pdfJobId : csvJobId;

  const { hasAdminAccess } = useCurrentUserRole();

  // Polling effect
  useEffect(() => {
    if (!currentJobId || !currentJobStatus?.job) return;

    const { status } = currentJobStatus.job;
    if (status === 'completed' || status === 'failed') {
      // Job finished, refetch all jobs to update history
      void refetchAllJobs();
      return;
    }

    // Continue polling if job is still processing
    if (status === 'processing' || status === 'queued') {
      const interval = setInterval(() => {
        if (exportFormat === 'pdf') {
          void refetchPdfStatus();
        } else {
          void refetchCsvStatus();
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [
    currentJobId,
    currentJobStatus?.job,
    refetchAllJobs,
    exportFormat,
    refetchPdfStatus,
    refetchCsvStatus,
  ]);

  const handleGenerate = async () => {
    if (!selectedSession?.value) return;

    try {
      let result;
      if (exportFormat === 'pdf') {
        result = await generatePDFMutation.mutateAsync({
          sessionId: selectedSession.value,
          showBalance,
        });
        setPdfJobId(result.jobId);
      } else {
        result = await generateCSVMutation.mutateAsync({
          sessionId: selectedSession.value,
          showBalance,
        });
        setCsvJobId(result.jobId);
      }
    } catch (error) {
      console.error('Failed to start generation:', error);
    }
  };

  const handleDownload = (downloadUrl: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetDialog = () => {
    setPdfJobId(null);
    setCsvJobId(null);
    setSelectedSession(null);
    setExportFormat('pdf');
    setShowBalance(false);
    setPdfDownloaded(false);
    setCsvDownloaded(false);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetDialog();
    }
    onOpenChange(open);
  };

  const isGenerating =
    generatePDFMutation.isPending || generateCSVMutation.isPending;
  const jobStatus = currentJobStatus?.job?.status;
  const isJobInProgress = jobStatus === 'processing' || jobStatus === 'queued';
  const isJobCompleted = jobStatus === 'completed';
  const isJobFailed = jobStatus === 'failed';

  // Check if there's an active job for the current format
  const hasActiveJob = currentJobId !== null;

  // Check if current format has been downloaded
  const currentFormatDownloaded = exportFormat === 'pdf' ? pdfDownloaded : csvDownloaded;

  // Check if both formats are downloaded
  const bothDownloaded = pdfDownloaded && csvDownloaded;

  // Calculate progress
  const progress =
    currentJobStatus?.job && currentJobStatus.job.totalClasses > 0
      ? Math.round(
          (currentJobStatus.job.processedClasses /
            currentJobStatus.job.totalClasses) *
            100,
        )
      : 0;

  const handleStartNew = () => {
    if (exportFormat === 'pdf') {
      setPdfJobId(null);
      setPdfDownloaded(false);
    } else {
      setCsvJobId(null);
      setCsvDownloaded(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Download Attendance Sheets</DialogTitle>
          <DialogDescription>
            Generate and download attendance sheets for all classes in a
            session. Choose your preferred format and options below.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Session Selection */}
          <div className='space-y-1.5'>
            <label
              htmlFor='session-selector'
              className='text-sm font-medium text-slate-700'
            >
              Session
            </label>
            <SessionSelector
              session={selectedSession}
              onChange={setSelectedSession}
              disabled={hasActiveJob}
            />
          </div>

          {/* Export Format Selection */}
          <div className='space-y-1.5'>
            <label className='text-sm font-medium text-slate-700'>
              Export Format
            </label>
            <div className='flex gap-2'>
              <Button
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  setExportFormat('pdf');
                }}
                disabled={bothDownloaded}
                className='flex items-center gap-2'
              >
                <FileText className='size-4' />
                PDF
                {pdfJobId && (
                  <span className='ml-1 text-xs'>
                    {pdfJobStatus?.job?.status === 'completed' && '✓'}
                  </span>
                )}
              </Button>
              <Button
                variant={exportFormat === 'csv' ? 'default' : 'outline'}
                size='sm'
                onClick={() => {
                  setExportFormat('csv');
                }}
                disabled={bothDownloaded}
                className='flex items-center gap-2'
              >
                <FileSpreadsheet className='size-4' />
                CSV
                {csvJobId && (
                  <span className='ml-1 text-xs'>
                    {csvJobStatus?.job?.status === 'completed' && '✓'}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Show Balance Option */}
          {hasAdminAccess && (<div className='flex items-center space-x-2'>
            <input
              type='checkbox'
              id='show-balance'
              checked={showBalance}
              onChange={(e) => {
                setShowBalance(e.target.checked);
              }}
              disabled={hasActiveJob}
              className='rounded border-gray-300'
            />
            <label htmlFor='show-balance' className='text-sm text-slate-700'>
              Include student balance information
            </label>
          </div>)}

          {/* Job Status and Progress */}
          {currentJobId && currentJobStatus?.job && (
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm font-medium'>Generation Status</span>
                <Badge
                  variant={
                    isJobCompleted
                      ? 'default'
                      : isJobFailed
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {currentJobStatus.job.status}
                </Badge>
              </div>

              {isJobInProgress && (
                <div className='space-y-2'>
                  <div className='flex justify-between text-sm text-slate-600'>
                    <span>Progress</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className='h-2' />
                  <div className='flex justify-between text-xs text-slate-500'>
                    <span>
                      Processed: {currentJobStatus.job.processedClasses}/
                      {currentJobStatus.job.totalClasses} classes
                    </span>
                    {currentJobStatus.job.failedClasses > 0 && (
                      <span className='text-amber-600'>
                        {currentJobStatus.job.failedClasses} failed
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isJobCompleted &&
                currentJobStatus.job.downloadUrl &&
                !currentFormatDownloaded && (
                  <Alert>
                    <Download className='size-4' />
                    <AlertDescription>
                      Generation complete! Your {exportFormat.toUpperCase()}{' '}
                      file is ready for download.
                    </AlertDescription>
                  </Alert>
                )}

              {isJobFailed && (
                <Alert variant='destructive'>
                  <AlertDescription>
                    Generation failed:{' '}
                    {currentJobStatus.job.error ?? 'Unknown error'}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className='flex-col gap-4 sm:flex-col'>
          {bothDownloaded && (
            <Alert>
              <AlertDescription className='text-xs'>
                You have downloaded both PDF and CSV files. Click "Generate New"
                to start a new generation or close this dialog.
              </AlertDescription>
            </Alert>
          )}
          <div className='flex w-full justify-end gap-2'>
            {isJobFailed ? (
              <>
                <DialogClose asChild>
                  <Button variant='outline'>Cancel</Button>
                </DialogClose>
                <Button onClick={handleStartNew}>Try Again</Button>
              </>
            ) : isJobCompleted && !currentFormatDownloaded ? (
              <>
                <Button variant='outline' onClick={handleStartNew}>
                  Generate New
                </Button>
                <Button
                  onClick={() => {
                    if (currentJobStatus?.job.downloadUrl) {
                      handleDownload(currentJobStatus.job.downloadUrl);
                      // Mark this format as downloaded
                      if (exportFormat === 'pdf') {
                        setPdfDownloaded(true);
                      } else {
                        setCsvDownloaded(true);
                      }
                    }
                  }}
                  disabled={!currentJobStatus?.job.downloadUrl}
                >
                  <Download className='mr-2 size-4' />
                  Download {exportFormat.toUpperCase()}
                </Button>
              </>
            ) : bothDownloaded ? (
              <>
                <DialogClose asChild>
                  <Button variant='outline'>Close</Button>
                </DialogClose>
                <Button onClick={resetDialog}>Generate New</Button>
              </>
            ) : currentFormatDownloaded ? (
              <>
                <DialogClose asChild>
                  <Button variant='outline'>Close</Button>
                </DialogClose>
                <Button onClick={handleStartNew}>
                  Generate New {exportFormat.toUpperCase()}
                </Button>
              </>
            ) : (
              <>
                <DialogClose asChild>
                  <Button variant='outline'>Cancel</Button>
                </DialogClose>
                <Button
                  onClick={handleGenerate}
                  disabled={
                    !selectedSession ||
                    isGenerating ||
                    isJobInProgress ||
                    hasActiveJob
                  }
                >
                  {isGenerating && (
                    <Loader2 className='mr-2 size-4 animate-spin' />
                  )}
                  {isJobInProgress
                    ? 'Generating...'
                    : hasActiveJob
                      ? `${exportFormat.toUpperCase()} Already Generated`
                      : `Generate ${exportFormat.toUpperCase()}`}
                </Button>
              </>
            )}
          </div>

          {/* Job History - Below Generate Button */}
          {allJobs?.jobs && allJobs.jobs.length > 0 && (
            <div className='w-full space-y-2 border-t pt-4'>
              <h4 className='text-sm font-medium text-slate-700'>
                Download History
              </h4>
              <div className='max-h-40 space-y-2 overflow-y-auto'>
                {allJobs.jobs.slice(0, 5).map((job) => (
                  <div
                    key={job.jobId}
                    className='flex items-center justify-between rounded-lg border bg-slate-50/50 p-3 text-xs transition-colors hover:bg-slate-100/50'
                  >
                    <div className='flex flex-1 items-center gap-3'>
                      {job.type && (
                        <Badge variant='outline' className='text-xs uppercase'>
                          {job.type}
                        </Badge>
                      )}
                      <div className='flex flex-col gap-0.5'>
                        {job.sessionName && (
                          <span className='font-medium text-slate-700'>
                            {job.sessionName}
                          </span>
                        )}
                        <span className='text-slate-600'>
                          {job.processedClasses}/{job.totalClasses} classes
                        </span>
                        {job.completedAt && (
                          <span className='text-[10px] text-slate-500'>
                            {new Date(job.completedAt).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: '2-digit',
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    {job.status === 'completed' && job.downloadUrl && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => {
                          if (job.downloadUrl) {
                            handleDownload(job.downloadUrl);
                          }
                        }}
                        className='h-8 gap-1.5 px-3'
                      >
                        <Download className='size-3.5' />
                        Download
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
